// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../../../components/ui/Button";
import { saveSubPlan, getPlanLanguages } from "../../../../../../api/museum";
import Dropdown from "../../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../../../context/useGlobalState";
import { DropdownOption, RoomLanguageProps, RouteParams } from "../../../../../../types/screensTypes";
import floorEdit from "./FloorEdit.module.css";
import stepStyles from "../../../createStep.module.css";

const RoomLanguage: React.FC<RoomLanguageProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { roomId } = useParams<RouteParams>();
    const { data, loading, error } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const saveRoom = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const subPlanId = !roomId || roomId === "" || roomId === "null" ? null : roomId;
        const trackId = trackData.trackId;
        if (!token || !trackId || !selectedContentLanguage) return;
        const saveRoomInfo = await saveSubPlan({
            token,
            trackId,
            subplanId: subPlanId,
            language: selectedContentLanguage,
        });
        if (saveRoomInfo && saveRoomInfo.subPlanId) {
            const id = saveRoomInfo.subPlanId;
            navigateToNext(id, selectedContentLanguage);
        }
    };

    const getExistingLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !roomId) return;
        const languages = await getPlanLanguages({ token, subplanId: roomId });
        const languagesArray = languages?.map((lang: { id: string }) => lang.id);
        setExistingLanguages(languagesArray || []);
    };

    useEffect(() => {
        if (roomId && roomId !== "" && roomId !== "null") {
            getExistingLanguagesAPI();
        }
    }, [roomId]);

    const languagesFiltered: DropdownOption[] = [
        ...((data.languagesAI as DropdownOption[] | undefined)?.filter((lang) =>
            existingLanguages.includes(lang.value),
        ) || []),
        { label: t("addANewLanguage"), value: "add-new-language" },
    ];

    const languagesUnfiltered: DropdownOption[] =
        (data.languagesAI as DropdownOption[] | undefined)?.filter((lang) => !existingLanguages.includes(lang.value)) ||
        [];

    const handleLanguageChange = (selectedOption: string): void => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true);
            setSelectedContentLanguage(null);
        } else {
            setShowAddLanguageDropdown(false);
            setSelectedContentLanguage(selectedOption);
        }
    };

    return (
        <div className={`experience-floor-edit ${floorEdit.root}`}>
            <TitleSubtitle
                title={t("selectYourLanguage")}
                subtitle={""}
                addMargin={false}
                classNameTitle={floorEdit.pageTitle}
            />
            <h5 className={`experience-floor-label ${floorEdit.label}`}>{t("existingLanguages")}</h5>
            <Dropdown
                label=""
                options={languagesFiltered}
                onChange={handleLanguageChange}
                loading={loading}
                error={error ? true : false}
                placeholder={t("language")}
                value={selectedContentLanguage}
                isWithoutMargin
            />
            {showAddLanguageDropdown && (
                <>
                    <h5
                        className={`experience-floor-label experience-floor-label--gap ${floorEdit.label} ${floorEdit.labelGap}`}
                    >
                        {t("selectANewLanguage")}
                    </h5>
                    <Dropdown
                        label=""
                        options={languagesUnfiltered}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isWithoutMargin
                    />
                </>
            )}
            <div className={`${stepStyles.stepButton} ${floorEdit.actions}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveRoom}
                />
            </div>
        </div>
    );
};

export default RoomLanguage;
